import React from 'react';

import { LomavisShortenedNumber } from '@/lomavisSharedUi/atoms/utils/numbers/LomavisShortenedNumber';

type AudienceEstimateResponse = {
  audience_size?: number | null;
  audience_size_lower_bound?: number | null;
  audience_size_upper_bound?: number | null;
};

const getPositiveNumberOrNull = (value: unknown) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) && numericValue > 0
    ? Math.round(numericValue)
    : null;
};

const getAudienceEstimateNumbers = (
  estimate: AudienceEstimateResponse | null
) => {
  if (!estimate) {
    return {
      lowerBound: null,
      upperBound: null,
      audienceSize: null
    };
  }

  return {
    lowerBound: getPositiveNumberOrNull(estimate.audience_size_lower_bound),
    upperBound: getPositiveNumberOrNull(estimate.audience_size_upper_bound),
    audienceSize: getPositiveNumberOrNull(estimate.audience_size)
  };
};

export const hasAudienceEstimate = (
  estimate: AudienceEstimateResponse | null
) => {
  const { lowerBound, upperBound, audienceSize } =
    getAudienceEstimateNumbers(estimate);

  return Boolean(lowerBound || upperBound || audienceSize);
};

type AdManagerAudienceEstimateProps = {
  estimate: AudienceEstimateResponse | null;
};

const AudienceNumber = ({ value }: { value: number }) => (
  <LomavisShortenedNumber value={value} compactLength={1} />
);

export const AdManagerAudienceEstimate: React.FC<
  AdManagerAudienceEstimateProps
> = ({ estimate }) => {
  const { lowerBound, upperBound, audienceSize } =
    getAudienceEstimateNumbers(estimate);

  if (lowerBound && upperBound) {
    return lowerBound === upperBound ? (
      <AudienceNumber value={lowerBound} />
    ) : (
      <>
        <AudienceNumber value={lowerBound} /> -{' '}
        <AudienceNumber value={upperBound} />
      </>
    );
  }

  if (upperBound) return <AudienceNumber value={upperBound} />;
  if (lowerBound) return <AudienceNumber value={lowerBound} />;
  if (audienceSize) return <AudienceNumber value={audienceSize} />;

  return <>-</>;
};
